import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api.ts";
import { CODIGOS_SUGERIBLES, leerMonto } from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";

interface Arancel { codigo: string; nombre: string; precio: number; activo: boolean; sugerirPor: string | null }
const aTexto = (centavos: number) => (centavos / 100).toFixed(2);

/**
 * Tratamientos y precios de la clínica (el arancel). Los cambios valen para los planes nuevos y para los procedimientos
 * que se agreguen después; lo que ya está en un plan conserva su valor. No se borra: se desactiva, así los planes y las facturas
 * antiguos siguen intactos.
 */
export function PanelTratamientos() {
  const [lista, setLista] = useState<Arancel[] | null>(null);
  const [ok, setOk] = useState("");
  const [filtro, setFiltro] = useState("");
  const [nuevo, setNuevo] = useState({ codigo: "", nombre: "", precio: "" });
  const a = useAccion();
  useEffect(() => { api<Arancel[]>("GET", "/api/aranceles").then(setLista).catch(a.setError); }, []);

  async function guardar(x: Arancel, cambios: Partial<Pick<Arancel, "nombre" | "activo" | "sugerirPor">> & { precio?: string }): Promise<boolean> {
    const nombre = (cambios.nombre ?? x.nombre).trim();
    const precio = cambios.precio ?? aTexto(x.precio);
    if (!nombre) { a.setError(new Error("El nombre del tratamiento no puede quedar vacío.")); return false; }
    if (leerMonto(precio) === null) { a.setError(new Error("El precio no es válido: escribe un monto como 45 o 45,50.")); return false; }
    const r = await a.correr(() => api<Arancel[]>("PUT", `/api/aranceles/${x.codigo}`, { nombre, precio, activo: cambios.activo ?? x.activo, ...("sugerirPor" in cambios ? { sugerirPor: cambios.sugerirPor } : {}) }));
    if (!r) return false;
    setLista(r); setOk(`Guardado: ${x.codigo}.`);
    return true;
  }

  async function agregar(e: FormEvent) {
    e.preventDefault();
    const codigo = nuevo.codigo.trim().toUpperCase();
    if (!/^[A-Z0-9]{1,10}$/.test(codigo)) return a.setError(new Error("El código usa hasta 10 letras o números, sin espacios (por ejemplo BLQ1)."));
    if (lista?.some((x) => x.codigo === codigo)) return a.setError(new Error(`Ya existe el código ${codigo}. Búscalo en la lista para cambiarlo.`));
    if (!nuevo.nombre.trim()) return a.setError(new Error("Escribe el nombre del tratamiento."));
    if (leerMonto(nuevo.precio) === null) return a.setError(new Error("El precio no es válido: escribe un monto como 45 o 45,50."));
    const r = await a.correr(() => api<Arancel[]>("PUT", `/api/aranceles/${codigo}`, { nombre: nuevo.nombre.trim(), precio: nuevo.precio, activo: true }));
    if (r) { setLista(r); setNuevo({ codigo: "", nombre: "", precio: "" }); setFiltro(""); setOk(`Tratamiento agregado: ${codigo}.`); }
  }

  if (!lista) return <Seccion titulo="Tratamientos y precios"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;
  const sugeribles = CODIGOS_SUGERIBLES.map((codigo) => lista.find((x) => x.codigo === codigo)).filter((x): x is Arancel => !!x);
  const t = filtro.trim().toLowerCase();
  const visibles = t ? lista.filter((x) => x.codigo.toLowerCase().includes(t) || x.nombre.toLowerCase().includes(t)) : lista;
  return (
    <Seccion titulo="Tratamientos y precios">
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Los precios en dólares que Donna usa en el plan de tratamiento y el presupuesto. Un cambio vale para los planes nuevos y para los
        procedimientos que agregues después; lo que ya está en un plan conserva su valor (ahí puedes ajustarlo a mano). Para dejar de ofrecer
        un tratamiento, desactívalo: no se borra, así los planes y las facturas anteriores no cambian.
      </p>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        <b>Sugerencias del odontograma:</b> en la última columna puedes elegir que un tratamiento se sugiera automáticamente en lugar de uno de la lista base.
        Por ejemplo, «Corona de zirconio» en lugar de «COR · Corona metal-porcelana»: cuando marques una corona por realizar, el plan propondrá la de zirconio.
        Cada procedimiento tiene un solo reemplazo (el último que elijas). Los que ya están pendientes en un plan se actualizan la próxima vez que se
        sincronice con el odontograma; los realizados no cambian.
      </p>
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      <AvisoError error={a.error} />
      <form className="rejilla" onSubmit={agregar} aria-label="Agregar tratamiento">
        <label className="campo">Código<input className="entrada mono" maxLength={10} placeholder="BLQ1" value={nuevo.codigo} onChange={(e) => { setNuevo({ ...nuevo, codigo: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }); setOk(""); }} /></label>
        <label className="campo">Nombre del tratamiento<input className="entrada" maxLength={120} value={nuevo.nombre} onChange={(e) => { setNuevo({ ...nuevo, nombre: e.target.value }); setOk(""); }} /></label>
        <label className="campo">Precio ($)<input className="entrada mono" inputMode="decimal" placeholder="0,00" value={nuevo.precio} onChange={(e) => { setNuevo({ ...nuevo, precio: e.target.value }); setOk(""); }} /></label>
        <div className="campo" style={{ justifyContent: "end" }}><button className="btn pri" type="submit" disabled={a.ocupado}>Agregar tratamiento</button></div>
      </form>
      <label className="campo">Buscar<input className="entrada" type="search" placeholder="Código o nombre" value={filtro} onChange={(e) => setFiltro(e.target.value)} /></label>
      <div className="tabla-scroll">
        <table className="tabla" aria-label="Tratamientos y precios">
          <thead><tr><th style={{ width: 84 }}>Código</th><th>Nombre</th><th style={{ width: 120 }}>Precio ($)</th><th style={{ width: 90 }}>Activo</th><th style={{ width: 250 }}>El odontograma lo sugiere en lugar de</th></tr></thead>
          <tbody>
            {visibles.map((x) => (
              <tr key={`${x.codigo}|${x.nombre}|${x.precio}|${x.activo}`} style={x.activo ? undefined : { opacity: 0.6 }}>
                <td className="mono">{x.codigo}</td>
                <td><input className="entrada" aria-label={`Nombre de ${x.codigo}`} defaultValue={x.nombre} maxLength={120}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== x.nombre) guardar(x, { nombre: v }).then((b) => { if (!b) e.target.value = x.nombre; }); }} /></td>
                <td><input className="entrada mono" aria-label={`Precio de ${x.codigo}`} inputMode="decimal" defaultValue={aTexto(x.precio)}
                  onBlur={(e) => { const v = leerMonto(e.target.value); if (v === null || v !== x.precio) guardar(x, { precio: e.target.value }).then((b) => { if (!b) e.target.value = aTexto(x.precio); }); }} /></td>
                <td><label><input type="checkbox" checked={x.activo} disabled={a.ocupado} onChange={(e) => guardar(x, { activo: e.target.checked })} /> {x.activo ? "Sí" : "No"}</label></td>
                <td>
                  <select className="entrada" aria-label={`Procedimiento que reemplaza ${x.codigo}`} value={x.sugerirPor ?? ""} disabled={a.ocupado}
                    onChange={(e) => guardar(x, { sugerirPor: e.target.value || null })}>
                    <option value="">No reemplaza a ninguno</option>
                    {sugeribles.filter((s) => s.codigo !== x.codigo).map((s) => <option key={s.codigo} value={s.codigo}>{s.codigo} · {s.nombre}</option>)}
                  </select>
                  {x.sugerirPor && !x.activo && <small className="hint">Desactivado: el odontograma no lo usa.</small>}
                </td>
              </tr>
            ))}
            {visibles.length === 0 && <tr><td colSpan={5} className="muted">No hay tratamientos que coincidan con la búsqueda.</td></tr>}
          </tbody>
        </table>
      </div>
    </Seccion>
  );
}
